import React, { useCallback, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  TableCell as MuiTableCell
} from '@mui/material';
import {
  getSettingsAsync,
  selectSettings,
  changeWorkTimeSettings,
  updateSettingsAsync
} from '../../redux/slices/settingsSlice';
import Title from '../../components/Title';
import TableCell from '../../components/TableCell';
import { DayIndex } from '../../resources/types/settingsTypes';
import { InputChangeEvent } from '../../resources/utils';

const TableHeaderRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: 'transparent',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px',
  '&:first-of-type': {
    paddingLeft: '20px'
  },
  '&:last-of-type': {
    paddingRight: '20px'
  }
}));

const PREFIX = 'WorkingHoursPage';

const classes = {
  title: `${PREFIX}-title`,
  subTitle: `${PREFIX}-subTitle`
};

const Root = styled('div')({
  [`& .${classes.title}`]: {
    marginBottom: 20
  },
  [`& .${classes.subTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10
  }
});

const days = ['НД', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'];

const WorkingHoursPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { workTime } = useSelector(selectSettings);

  const getSettings = useCallback(() => {
    dispatch(getSettingsAsync());
  }, [dispatch]);

  useEffect(() => {
    getSettings();
  }, [getSettings]);

  const changeTime = useCallback(
    (index: number, prop: 'from' | 'to') => (e: InputChangeEvent) => {
      dispatch(
        changeWorkTimeSettings({
          dayKey: String(index),
          prop,
          value: e.target.value
        })
      );
    },
    [dispatch]
  );

  const updateTimeSettings = useCallback(() => {
    if (!workTime) {
      return dispatch(updateSettingsAsync());
    }

    let isValid = true;
  
    for (let index = 0; index < days.length; index++) {
      const from = workTime[String(index) as DayIndex].from;
      const to = workTime[String(index) as DayIndex].to;
  
      if (to < from) {
        isValid = false;
        break;
      }
    }
  
    if (isValid) {
      dispatch(updateSettingsAsync());
    } else {
      alert('Введені робочі години не правильні!');
    }
  }, [dispatch, workTime]);
  
  return (
    <Root>
      <Paper 
        sx={{ 
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', 
          borderRadius: 2,
          padding: '20px'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Title>Робочі години</Title>
          <Button 
            color='primary' 
            variant='contained'
            onClick={updateTimeSettings}
            disabled={!workTime}
          >
            Зберегти
          </Button>
        </Box>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          Зверніть увагу що не може бути кінець дня о 00:00 а лише о 23:59 якщо початок дня не має значення 00:00. Значення 00:00 для початку та кінця використовуються лише для відключення перевірки робочого часу протягом дня!
        </Alert>
        
        {workTime && (
          <TableContainer sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableHeaderRow>
                  <TableHeaderCell>День</TableHeaderCell>
                  <TableHeaderCell>Початок</TableHeaderCell>
                  <TableHeaderCell>Кінець</TableHeaderCell>
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                {days.map((day, index) => (
                  <TableRow key={day}>
                    <TableCell>{day}</TableCell>
                    <TableCell>
                      <TextField
                        type='time'
                        value={workTime[String(index) as DayIndex].from}
                        onChange={changeTime(index, 'from')}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type='time'
                        value={workTime[String(index) as DayIndex].to}
                        onChange={changeTime(index, 'to')}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Root>
  );
};

export default WorkingHoursPage;
